import { HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { from, Observable } from 'rxjs';

import { catchError, map, mergeMap } from 'rxjs/operators';
import { throwError } from 'rxjs/internal/observable/throwError';
import { UserAuthenticationService } from '../user-authentication/user-authentication.service';
import { PreviewContextService, PreviewRequestBlockedError } from '../preview-context/preview-context.service';
import { UrlTokens } from './apidata.model';
import { NotificationsService } from '../notifications/notifications.service';
import { WorkspaceService } from '../workspace/workspace.service';
import { OrganizationCatalogService } from '../organization-catalog/organization-catalog.service';
import { SPCatalogService } from '../spcatalog/spcatalog.service';
import { SignalRService } from '../signal-r/signal-r.service';
import { ConsentsPermissionsService } from 'src/app/modules/workspace/services/consents-permissions/consents-permissions.service';
import { UserTenantConsentApplicationEnum } from 'src/app/modules/workspace/models/consents-permissions.model';
import { HEADERNAMEAPPLICATIONPRODUCT } from 'src/app/models/constants.model';
import { environment } from 'src/environments/environment';
import { PRIMARY_OUTLET, Router } from '@angular/router';


@Injectable({
	providedIn: 'root'
})
export class HttpRequestInterceptor implements HttpInterceptor {
	private router = inject(Router);
	private userAuthenticationService = inject(UserAuthenticationService);
	private previewContext = inject(PreviewContextService);
	private workspaceService = inject(WorkspaceService);
	private notification = inject(NotificationsService);
	private catalogService = inject(OrganizationCatalogService);
	private SPCatalogService = inject(SPCatalogService);
	private signalRService = inject(SignalRService);
	private consentPermissionsService = inject(ConsentsPermissionsService);


	public intercept<Type>(
		req: HttpRequest<Type>,
		next: HttpHandler
	): Observable<HttpEvent<Type>> {
		const publicCatalogRequest = this.isPublicCatalogPage() && !this.shouldSkipAuthenticationWait(req.url);
		return from(publicCatalogRequest ? Promise.resolve() : this.ensureAuthenticationResolved(req.url)).pipe(
			mergeMap(() => this.forwardRequest(req, next, publicCatalogRequest))
		);
	}

	private forwardRequest<Type>(
		req: HttpRequest<Type>,
		next: HttpHandler,
		publicCatalogRequest: boolean
	): Observable<HttpEvent<Type>> {

		let url = req.url;
		const accessToken = publicCatalogRequest
			? this.userAuthenticationService.getPublicCatalogUserAuthenticatedUserAccessToken()
			: this.userAuthenticationService.getAuthenticatedUserAccessToken();

		let headers = new HttpHeaders({
			"app-portal": "BindTuning",
		});
		if (accessToken) {
			headers = headers.set("Authorization", "Bearer " + accessToken);
		}

		const signalRConnectionId = this.signalRService.getAdminAppConnectionId()
		if (signalRConnectionId) {
			headers = headers.append("connectionId", signalRConnectionId);
		}

		const workspaceId = publicCatalogRequest
			? this.userAuthenticationService.getPublicCatalogCurrentWorkspaceId()
			: this.workspaceService.getCurrentUserWorkspaceId();
		if (workspaceId) {
			headers = headers.append("workspaceId", workspaceId);
		}

		let previewUserId: string | undefined;
		try {
			previewUserId = this.previewContext.handlePreviewMode(req.method, req.urlWithParams,
				this.previewContext.getPreviewUserId() !== undefined && this.userAuthenticationService.isBindTuningAdmin());
		} catch (error) {
			if (error instanceof PreviewRequestBlockedError) this.notification.showError(error.message);
			throw error;
		}

		if (previewUserId) {
			headers = headers.append("preview", previewUserId);
		}

		const currentCatalog = this.catalogService.getCurrentCatalog() || this.SPCatalogService.getCatalogId();
		if (currentCatalog) {
			headers = headers.append("catalogId", currentCatalog);
		}

		if (url.includes(UrlTokens.userId)) {
			if (previewUserId !== undefined) {
				url = url.replace(UrlTokens.userId, previewUserId);
			} else {
				let userId = publicCatalogRequest
					? this.userAuthenticationService.getPublicCatalogUserAuthenticatedUserId()
					: this.userAuthenticationService.getAuthenticatedUserId();
				if (!userId && !publicCatalogRequest) {
					const consentAutomate = this.consentPermissionsService.getCachedUserConsentByApplication(UserTenantConsentApplicationEnum.Automate);
					userId = consentAutomate?.identity.id ?? "";

				}
				url = url.replace(UrlTokens.userId, userId);
			}
		}

		if (url.includes(UrlTokens.AuthenticationKey) || url.includes(UrlTokens.userEmail)) {
			if (req.headers.has(HEADERNAMEAPPLICATIONPRODUCT)) {
				const productHeader = req.headers.get(HEADERNAMEAPPLICATIONPRODUCT);
				const product = productHeader ? productHeader as UserTenantConsentApplicationEnum : undefined;
				if (product) {
					const consent = this.consentPermissionsService.getCachedUserConsentByApplication(product);
					url = url.replace(UrlTokens.AuthenticationKey, consent?.key ?? "");
					url = url.replace(UrlTokens.userEmail, consent?.identity.email ?? "");

					if (url.includes(UrlTokens.TenantId)) {
						const tenantId = consent?.tenant.id ?? "";
						url = url.replace(UrlTokens.TenantId, tenantId);
					}
				}
			}
		}

		if (url.includes(UrlTokens.WorkspaceId) && workspaceId) {
			url = url.replace(UrlTokens.WorkspaceId, workspaceId)
		}

		if (url.includes(UrlTokens.TenantId)) {
			const tenantId = this.workspaceService.getCurrentUserWorkspaceTenantId();
			url = url.replace(UrlTokens.TenantId, tenantId);
		}

		//Validate if url contains {...} and throw if so
		const regex = /{(.*?)}/g;
		const matches = url.match(regex);
		if (matches && matches.length > 0) {
			throw new Error(`The url: ${url} contains invalid tokens: ${matches.join(", ")}`);
		}

		// If the partner Select the client to context add headers to identify on request
		const duplicate = req.clone({ url, headers });

		return next.handle(duplicate).pipe(
			map((event: HttpEvent<Type>) => {
				return event;
			}),
			catchError((error: HttpErrorResponse) => {

				if (error.status === 401) {
					sessionStorage.clear();
				}

				if (error.status > 404 && error.status !== 409) {
					if (error.error.message) {
						this.notification.showError(error.error.details, error.error.message);
					}
				}
				else if (error.status === 0) {
					this.notification.showError("The connection with the server is not valid at this moment, please try again later.", "An error occurred on our APIS");
				}

				return throwError(error);
			}));
	}

	private isPublicCatalogPage(): boolean {
		// Use the destination during navigation, including the initial page load.
		const navigation = this.router.currentNavigation();
		const url = navigation?.finalUrl ?? navigation?.initialUrl ?? this.router.parseUrl(this.router.url);
		const segments = url.root.children[PRIMARY_OUTLET]?.segments ?? [];
		const page = segments[0]?.path;
		return (page === 'catalog' && segments.length > 1) || page === 'tenant' || page === 'spcatalog' || page === 'approval';
	}

	private async ensureAuthenticationResolved(requestUrl: string): Promise<void> {
		if (this.shouldSkipAuthenticationWait(requestUrl)) {
			return;
		}

		const authenticated = await this.userAuthenticationService.verifyIfUserIsAuthenticatedAsync();
		if (authenticated === undefined) {
			await this.userAuthenticationService.getAuthenticatedUser().catch(() => undefined);
		}
	}

	private shouldSkipAuthenticationWait(requestUrl: string): boolean {
		const bindTuningIdUrl = environment.urls.bindtuningId.toLowerCase();
		const url = requestUrl.toLowerCase();
		return url.startsWith(bindTuningIdUrl) && (url.includes('authenticate') || url.includes('/user/logout'));
	}

}
